import { checkType, CtyButton, CtyShowToast, useScreenWidth, ErrorBoundary, confInputType, GlobalConfiguration, CopyCodeField, optionalCodePayload } from '@circuitry-ai/doc-ui';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { categoryType, useCreateAiWorkersMutation, useUpdateAiWorkersMutation, useTypedSelector, useGetAnalyticsReportsListQuery, useGetAiWorkerByIdQuery, RouteState, useGetMlEndPointsQuery, useGetAnalystSkillsQuery, useGetPyAnalystEndpointsQuery, useEntityResourcePermissions, parseSkillsAttrValue } from '@circuitry-ai/doc-data';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { DatePicker } from '@nextui-org/react';
import { parseDate, type CalendarDate } from '@internationalized/date';
import { AnalystTabs } from './analystBoard';
import { DECISION_ANALYST_TYPE, useAnalystInsight, workerAttr } from './analystRuntime';
import {
  PYANALYST_ENDPOINT_ATTR,
  PYANALYST_ENDPOINT_CONFIG_ATTR,
  applyDateFiltersToPayload,
  buildEndpointConfigFromSelection,
  extractDateFiltersFromPayload,
  parsePyAnalystEndpointConfig,
  type PyAnalystEndpointSpec,
} from './pyanalystUtils';

/* eslint-disable-next-line */

export interface UserFormDetails {
  name?: string;
  description?: string;
  report_id: string;
}

export function AnalystConfiguration() {

  const screenWidth = useScreenWidth();
  const token = useTypedSelector((state) => state.auth.token);
  const [searchParams] = useSearchParams();

  const { mode } = useParams<{ mode: RouteState; }>();
  const isCreateAnalyst = mode === RouteState.CREATE;

  const aiworkderId = searchParams.get('aiworkerId');

  const [skip, setSkip] = useState<boolean>(true);

  const { data: analyticsReports, error: analyticsError, isLoading: analyticsLoading, isFetching: analyticsFetching } = useGetAnalyticsReportsListQuery(undefined, { skip, refetchOnMountOrArgChange: true });
  const { data: mlEndPointList, error: mlEndPointListError, isLoading: isMlEndPointLoading, isFetching: isMlEndPointFetching } = useGetMlEndPointsQuery(undefined, { skip, refetchOnMountOrArgChange: true });
  const { data: analystSkillsList, error: analystSkillsError, isLoading: isAnalystSkillsLoading, isFetching: isAnalystSkillsFetching } = useGetAnalystSkillsQuery(undefined, { skip, refetchOnMountOrArgChange: true });
  const { data: analystData, error: analystError, isLoading: isAnalystDataLoading, isFetching: isAnalystDataFetching, refetch: refetchAnalystData } = useGetAiWorkerByIdQuery(aiworkderId ?? '', { skip: isCreateAnalyst, refetchOnMountOrArgChange: true });
  // Where AI Insight is on, a decision analyst's runtime is switched here (the app provides the control).
  const RuntimeSwitch = useAnalystInsight()?.RuntimeSwitch;
  const showRuntimeSwitch = Boolean(RuntimeSwitch) && !isCreateAnalyst && Boolean(analystData)
    && workerAttr(analystData, 'analyst_type') === DECISION_ANALYST_TYPE;

  useEffect(() => {
    if (token && skip) {
      setSkip(false);
    }
  }, [skip, token]);

  const AnalystFormWithLoadingAndError = ErrorBoundary(AnalystConfigurationForm);
  // NOSONAR
  const analystFormLoading = analyticsLoading || analyticsFetching || isAnalystDataLoading || isAnalystDataFetching || isMlEndPointLoading || isMlEndPointFetching || isAnalystSkillsLoading || isAnalystSkillsFetching;
  const isError = analyticsError || analystError || mlEndPointListError || analystSkillsError;

  return (
    <>
      <AnalystFormWithLoadingAndError
        isLoading={analystFormLoading}
        error={isError}
        analyticsReports={analyticsReports}
        analystData={analystData}
        mlEndPointList={mlEndPointList}
        analystSkillsList={analystSkillsList}
        tokenReady={!skip}
        isCreateAnalyst={isCreateAnalyst}
        screenWidth={screenWidth}
        aiworkderId={aiworkderId}
      />
      {showRuntimeSwitch && RuntimeSwitch && analystData ? (
        <div className="py-4 lg:w-[70%]">
          <RuntimeSwitch analyst={analystData} onChanged={() => void refetchAnalystData()} />
        </div>
      ) : null}
    </>
  );
}

const AnalystConfigurationForm = ({ analyticsReports, screenWidth, analystData, isCreateAnalyst, mlEndPointList, analystSkillsList, aiworkderId, tokenReady }: any) => {
  const analystPermissions = useEntityResourcePermissions('analyst', isCreateAnalyst ? undefined : aiworkderId ?? undefined); // NOSONAR - hooks must be called unconditionally; used in edit mode JSX

  const navigate = useNavigate();
  const agentFormRef = useRef<any>(null);

  const [analystCode, setAnalystCode] = useState<string>('');
  const [reportDetails, setReportDetails] = useState<any>(null);
  const [analystType, setAnalystType] = useState<string>('');
  const [reportType, setReportType] = useState<string>('');
  const [pyAnalystSelectedEndpoint, setPyAnalystSelectedEndpoint] = useState<string>('');
  const [pyAnalystDateFrom, setPyAnalystDateFrom] = useState<CalendarDate | null>(null);
  const [pyAnalystDateTo, setPyAnalystDateTo] = useState<CalendarDate | null>(null);
  const [pyAnalystDateColumn, setPyAnalystDateColumn] = useState<string>('');

  const [updateAiWorkers, { isLoading: updatingAiWorkersData }] = useUpdateAiWorkersMutation();
  const [createAiWorkers, { isLoading: creatingAiWorkersData }] = useCreateAiWorkersMutation();

  const shouldLoadPyAnalystEndpoints =
    tokenReady && (analystType === 'pyanalyst' || reportDetails?.analyst_type === 'pyanalyst');
  const { data: pyAnalystEndpoints } = useGetPyAnalystEndpointsQuery(undefined, {
    skip: !shouldLoadPyAnalystEndpoints,
    refetchOnMountOrArgChange: true,
  });

  const syncPyAnalystDatesFromEndpoint = useCallback(
    (endpointKey: string) => {
      if (!endpointKey || !pyAnalystEndpoints) {
        setPyAnalystDateFrom(null);
        setPyAnalystDateTo(null);
        setPyAnalystDateColumn('');
        return;
      }
      const spec = pyAnalystEndpoints[endpointKey] as PyAnalystEndpointSpec | undefined;
      const dateFilters = extractDateFiltersFromPayload(
        (spec?.sample_payload ?? {}) as Record<string, unknown>
      );
      const primary = dateFilters[0];
      if (!primary) {
        setPyAnalystDateFrom(null);
        setPyAnalystDateTo(null);
        setPyAnalystDateColumn('');
        return;
      }
      setPyAnalystDateFrom(parseDate(primary.from));
      setPyAnalystDateTo(parseDate(primary.to));
      setPyAnalystDateColumn(primary.column);
    },
    [pyAnalystEndpoints]
  );

  useEffect(() => {
    if (!analyticsReports || !mlEndPointList || (!isCreateAnalyst && !analystData)) return;

    const reports = analyticsReports ?? [];
    const reportList = reports.map((report: any) => ({
      label: report.id,
      value: report.id
    }));

    const mlEndPointListOptions = mlEndPointList?.map((report: any) => ({
      label: report,
      value: report
    })) ?? [];

    const pyAnalystEndpointOptions = Object.keys(pyAnalystEndpoints ?? {}).map((key) => ({
      label: key,
      value: key,
    }));

    const attrMap = analystData?.aiworkers_attrs?.reduce((acc: Record<string, any>, item: any) => {
      acc[item.attr_name] = item;
      return acc;
    }, {} as Record<string, typeof analystData.aiworkers_attrs[number]>);

    const storedPyConfig = parsePyAnalystEndpointConfig(
      attrMap?.[PYANALYST_ENDPOINT_CONFIG_ATTR]?.attr_json
    );

    setReportDetails({
      aiworker_id: analystData?.aiworker_id ?? '',
      name: analystData?.name ?? '',
      description: analystData?.description ?? '',
      report_id: attrMap?.['report_id']?.attr_value ?? '',
      report_type: attrMap?.['report_type']?.attr_value ?? '',
      ml_endpoint: attrMap?.['ml_endpoint']?.attr_value ?? '',
      analyst_type: attrMap?.['analyst_type']?.attr_value ?? '',
      pyanalyst_endpoint:
        attrMap?.[PYANALYST_ENDPOINT_ATTR]?.attr_value ?? storedPyConfig?.key ?? '',
      skills: parseSkillsAttrValue(attrMap?.['skills']?.attr_value),
      reportIds: reportList,
      mlEndPointList: mlEndPointListOptions,
      pyAnalystEndpointList: pyAnalystEndpointOptions,
    });

    !isCreateAnalyst && setAnalystCode(analystData?.code ?? '');
    !isCreateAnalyst && setAnalystType(attrMap?.['analyst_type']?.attr_value ?? '');
    !isCreateAnalyst && setReportType(attrMap?.['report_type']?.attr_value ?? '');

  }, [analyticsReports, analystData, isCreateAnalyst, mlEndPointList, pyAnalystEndpoints]);

  useEffect(() => {
    if (!pyAnalystEndpoints) return;
    const pyAnalystEndpointOptions = Object.keys(pyAnalystEndpoints).map((key) => ({
      label: key,
      value: key,
    }));
    setReportDetails((previous: any) =>
      previous
        ? { ...previous, pyAnalystEndpointList: pyAnalystEndpointOptions }
        : previous
    );
  }, [pyAnalystEndpoints]);

  const analystConfigurationInputFields = useMemo(() => {
    const fields: any[] = [
      {
        label: 'Name',
        id: 'name',
        value: reportDetails?.name,
        required: true,
        inputType: confInputType.TEXT,
      },
      {
        label: 'Description',
        id: 'description',
        value: reportDetails?.description,
        required: true,
        inputType: confInputType.TEXTAREA,
        minRows: 4,
        maxRows: 6
      },
      {
        label: 'Analyst Type',
        id: 'analyst_type',
        value: reportDetails?.analyst_type,
        required: true,
        disabled: !isCreateAnalyst,
        inputType: confInputType.SINGLE_SELECT,
        options: [
          { label: 'Descriptive', value: 'descriptive' },
          { label: 'Prescriptive', value: 'prescriptive' },
          { label: 'Evaluations', value: 'evaluations' },
          { label: 'Decision', value: 'decision' },
          { label: 'Pyanalyst', value: 'pyanalyst' },
        ]
      }
    ];

    if (analystType === 'descriptive') {
      fields.push({
        label: 'Report ID',
        id: 'report_id',
        disabled: !isCreateAnalyst,
        value: reportDetails?.report_id,
        required: true,
        inputType: confInputType.SINGLE_SELECT,
        options: reportDetails?.reportIds ?? [],
      });
    } else if (analystType === 'prescriptive') {
      fields.push({
        label: 'ML Endpoint',
        id: 'ml_endpoint',
        disabled: !isCreateAnalyst,
        value: reportDetails?.ml_endpoint,
        required: true,
        inputType: confInputType.SINGLE_SELECT,
        options: reportDetails?.mlEndPointList ?? [],
      });
    } else if (analystType === 'evaluations') {
      fields.push({
        label: 'Report Type',
        id: 'report_type',
        value: reportType,
        required: true,
        inputType: confInputType.SINGLE_SELECT,
        options: [
          { label: 'Static', value: 'static' },
          { label: 'Interactive', value: 'interactive' },
        ],
      });
      if (reportType === 'static') {
        fields.push({
          label: 'Report ID',
          id: 'report_id',
          value: reportDetails?.report_id,
          required: true,
          inputType: confInputType.TEXT,
        });
      }
    } else if (analystType === 'decision') {
      fields.push({
        label: 'Skills',
        id: 'skills',
        disabled: !isCreateAnalyst,
        value: reportDetails?.skills ?? [],
        required: true,
        inputType: confInputType.MULTI_SELECT,
        placeholder: 'Select skills',
        options: (analystSkillsList ?? []).map((skill: string) => ({ label: skill, value: skill })),
      });
    } else if (analystType === 'pyanalyst') {
      fields.push({
        label: 'PyAnalyst Endpoint',
        id: 'pyanalyst_endpoint',
        disabled: !isCreateAnalyst,
        value: reportDetails?.pyanalyst_endpoint,
        required: true,
        inputType: confInputType.SINGLE_SELECT,
        options: reportDetails?.pyAnalystEndpointList ?? [],
      });
    }

    return fields;
  }, [reportDetails, analystType, reportType, isCreateAnalyst, analystSkillsList]);

  const handleFieldChange = (field: string, value: any) => {
    if (field === 'analyst_type') {
      setAnalystType(value);
      if (value !== 'evaluations') setReportType('');
      if (value !== 'pyanalyst') {
        setPyAnalystSelectedEndpoint('');
        setPyAnalystDateFrom(null);
        setPyAnalystDateTo(null);
        setPyAnalystDateColumn('');
      }
    }
    if (field === 'report_type') {
      setReportType(value);
    }
    if (field === 'pyanalyst_endpoint') {
      const endpointKey = String(value ?? '');
      setPyAnalystSelectedEndpoint(endpointKey);
      syncPyAnalystDatesFromEndpoint(endpointKey);
    }
  };

  const showPyAnalystCreateDateRange =
    isCreateAnalyst && analystType === 'pyanalyst' && !!pyAnalystDateColumn;
  const isPyAnalystCreateDateRangeInvalid =
    !!pyAnalystDateFrom &&
    !!pyAnalystDateTo &&
    pyAnalystDateFrom.toString() > pyAnalystDateTo.toString();

  const pyAnalystDateToErrorMessage = (() => {
    if (isPyAnalystCreateDateRangeInvalid) {
      return 'Date To should be greater than or equal to Date From';
    }
    if (!pyAnalystDateTo) {
      return 'Date To is required';
    }
    return '';
  })();

  useEffect(() => {
    if (!pyAnalystSelectedEndpoint || !pyAnalystEndpoints || pyAnalystDateColumn) return;
    syncPyAnalystDatesFromEndpoint(pyAnalystSelectedEndpoint);
  }, [pyAnalystSelectedEndpoint, pyAnalystEndpoints, pyAnalystDateColumn, syncPyAnalystDatesFromEndpoint]);

  const handleSaveAnalyst = async () => {
    try {
      const [agentValues] = await Promise.all([
        agentFormRef.current?.validateAndSubmit()
      ]);

      const { analyst_type, name, description, ml_endpoint, report_id, report_type, skills, pyanalyst_endpoint } = agentValues || {};
      const filteredAnalystReport = (analyticsReports ?? [])?.find((report: any) => report.id === report_id);

      const baseAttrs = [{ attr_name: 'analyst_type', attr_value: analyst_type }];
      let additionalAttrs: { attr_name: string, attr_value: string | string[]; }[] = [];

      if (analyst_type === 'descriptive') {
        additionalAttrs = [
          { attr_name: 'report_id', attr_value: report_id },
          { attr_name: 'dataset_id', attr_value: filteredAnalystReport?.datasetId },
          { attr_name: 'dataset_workspace_id', attr_value: filteredAnalystReport?.datasetWorkspaceId }
        ];
      } else if (analyst_type === 'prescriptive') {
        additionalAttrs = [
          { attr_name: 'ml_endpoint', attr_value: ml_endpoint }
        ];
      } else if (analyst_type === 'evaluations') {
        additionalAttrs = [{ attr_name: 'report_type', attr_value: report_type }];
        if (report_type === 'static') {
          additionalAttrs.push({ attr_name: 'report_id', attr_value: report_id });
        }
      } else if (analyst_type === 'decision') {
        additionalAttrs = [
          { attr_name: 'skills', attr_value: Array.isArray(skills) ? skills : [] }
        ];
      } else if (analyst_type === 'pyanalyst') {
        const endpointSpec = (pyAnalystEndpoints?.[pyanalyst_endpoint] ?? null) as PyAnalystEndpointSpec | null;
        if (!pyanalyst_endpoint || !endpointSpec?.function_url) {
          throw new Error('Please select a valid PyAnalyst endpoint');
        }
        const endpointConfig = buildEndpointConfigFromSelection(pyanalyst_endpoint, endpointSpec);
        const templateDateFilters = extractDateFiltersFromPayload(
          (endpointSpec.sample_payload ?? {}) as Record<string, unknown>
        );
        if (templateDateFilters.length > 0) {
          if (!pyAnalystDateFrom || !pyAnalystDateTo) {
            throw new Error('Please select both Date From and Date To');
          }
          if (pyAnalystDateFrom.toString() > pyAnalystDateTo.toString()) {
            throw new Error('Date From should be less than or equal to Date To');
          }
          const column = pyAnalystDateColumn || templateDateFilters[0].column;
          endpointConfig.sample_payload = applyDateFiltersToPayload(
            (endpointConfig.sample_payload ?? {}) as Record<string, unknown>,
            [
              {
                column,
                from: pyAnalystDateFrom.toString(),
                to: pyAnalystDateTo.toString(),
              },
              ...templateDateFilters.slice(1),
            ]
          );
        }
        additionalAttrs = [
          { attr_name: PYANALYST_ENDPOINT_ATTR, attr_value: pyanalyst_endpoint },
          {
            attr_name: PYANALYST_ENDPOINT_CONFIG_ATTR,
            attr_value: null,
            attr_json: endpointConfig,
          },
        ];
      }

      const aiworkers_attrs = [...baseAttrs, ...additionalAttrs];
      const payLoad = isCreateAnalyst ? {
        name,
        description,
        category: categoryType.ANALYST,
        type: 'product',
        aiworkers_attrs,
        ...optionalCodePayload(analystCode),
      } : {
        ...(name !== reportDetails?.name && { name }),
        ...(description !== reportDetails?.description && { description }),
        ...(reportDetails?.analyst_type === 'evaluations' && {
          aiworkers_attrs: [
            { attr_name: 'report_type', attr_value: report_type },
            ...(report_type === 'static' ? [{ attr_name: 'report_id', attr_value: report_id }] : []),
          ],
        }),
      };

      const response = isCreateAnalyst ? await createAiWorkers(payLoad) : await updateAiWorkers({ payLoad, aiworker_id: reportDetails.aiworker_id });
      CtyShowToast(checkType.success, response?.data?.message);
      isCreateAnalyst && navigate('/analytics');

    } catch (error) {
      CtyShowToast(checkType.error, (error as Error)?.message || 'Save Operation failed');
    }
  };

  const footerItems = () => {
    const isSaving = creatingAiWorkersData || updatingAiWorkersData;
    return (
      <div className={`flex flex-row ${isCreateAnalyst ? 'justify-end' : 'justify-between'} w-[70%] gap-3`}>
        <CtyButton variant="solid" color="primary" size="md" radius="sm" className={screenWidth <= 768 ? 'w-full' : ''} onPress={handleSaveAnalyst} isLoading={isSaving} isDisabled={isCreateAnalyst ? false : !analystPermissions.canUpdate}>
          {isCreateAnalyst ? 'Save' : 'Update'}
        </CtyButton>
        {!isCreateAnalyst && <CtyButton variant="bordered" color="primary" size="lg" radius="sm" onPress={() => {
          const params = new URLSearchParams({
            tab: AnalystTabs.DASHBOARD,
            aiworkerId: reportDetails?.aiworker_id ?? '',
          });
          if (reportDetails?.report_id) params.append('reportId', reportDetails?.report_id);
          if (
            reportDetails?.analyst_type === 'evaluations' ||
            reportDetails?.analyst_type === 'decision' ||
            reportDetails?.analyst_type === 'pyanalyst'
          ) {
            params.append('analystType', reportDetails?.analyst_type);
            if (reportDetails?.analyst_type === 'evaluations' && reportType) {
              params.append('reportType', reportType);
            }
          }
          navigate(`/analytics/${RouteState.VIEW}?${params.toString()}`);
        }} isDisabled={!analystPermissions.canUpdate}>
          Launch Analyst
        </CtyButton>}
      </div>
    );
  };

  const globalConfigurationProps = {
    inputs: analystConfigurationInputFields,
    formAlignmentStyle: 'flex-col gap-1',
    formStyle: 'py-2 lg:w-[70%]',
    onChange: handleFieldChange
  };

  return (
    <div className="flex flex-col h-full gap-3">
      <CopyCodeField
        label="Code"
        value={analystCode}
        className="py-2 lg:w-[70%]"
        isReadOnly={!isCreateAnalyst}
        isRequired={false}
        onValueChange={isCreateAnalyst ? setAnalystCode : undefined}
      />
      <GlobalConfiguration {...globalConfigurationProps} ref={agentFormRef} />
      {showPyAnalystCreateDateRange && 
        <div className="flex flex-row flex-wrap gap-3 items-end py-2 lg:w-[70%]">
          <DatePicker
            size="sm"
            label="Date From"
            labelPlacement="outside"
            value={pyAnalystDateFrom}
            onChange={(date) => setPyAnalystDateFrom(date ?? null)}
            isInvalid={!pyAnalystDateFrom}
            errorMessage={!pyAnalystDateFrom ? 'Date From is required' : ''}
            className="w-full sm:w-[150px]"
          />
          <DatePicker
            size="sm"
            label="Date To"
            labelPlacement="outside"
            value={pyAnalystDateTo}
            onChange={(date) => setPyAnalystDateTo(date ?? null)}
            isInvalid={!pyAnalystDateTo || isPyAnalystCreateDateRangeInvalid}
            errorMessage={pyAnalystDateToErrorMessage}
            className="w-full sm:w-[150px]"
          />
        </div>}
      {footerItems()}
    </div>
  );
};

export default AnalystConfiguration;